import { useEffect, useMemo, useRef, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import type { Lang, RawSignals, VerdictResult } from "./lib/types";
import { runVerdict } from "./lib/verdict";
import { initAds } from "./lib/ads";
import { isNative } from "./lib/native";
import { initAnalytics, track, lengthBucket } from "./lib/analytics";
import { archetypeColor } from "./lib/archetypeMeta";
import { STRINGS } from "./i18n/strings";
import Splash from "./screens/Splash";
import Vent from "./screens/Vent";
import Crumple from "./screens/Crumple";
import Loading from "./screens/Loading";
import Report from "./screens/Report";
import Detail from "./screens/Detail";
import ShareCard from "./screens/ShareCard";
import Crisis from "./screens/Crisis";
import BowOut from "./screens/BowOut";
import Settings from "./screens/Settings";
import Legal from "./screens/Legal";
import Ad from "./screens/Ad";
import InstallFab from "./screens/InstallFab";
import type { LegalKind } from "./content/legal";

export type Phase =
  | "splash"
  | "vent"
  | "settings"
  | "legal"
  | "crumple"
  | "loading"
  | "report"
  | "detail"
  | "ad"
  | "share"
  | "bowout"
  | "crisis";

const LANGS: Lang[] = ["ko", "en", "ja"];

function detectLang(): Lang {
  const saved = localStorage.getItem("bwek_lang") as Lang | null;
  if (saved && LANGS.includes(saved)) return saved;
  const l = navigator.language?.toLowerCase() ?? "en";
  if (l.startsWith("ko")) return "ko";
  if (l.startsWith("ja")) return "ja";
  return "en";
}

export default function App() {
  const [phase, setPhase] = useState<Phase>("splash");
  const [lang, setLangState] = useState<Lang>(detectLang());
  const [signals, setSignals] = useState<RawSignals | null>(null);
  const [result, setResult] = useState<VerdictResult | null>(null);
  const [adNext, setAdNext] = useState<"detail" | "share">("detail");
  const [legalKind, setLegalKind] = useState<LegalKind>("privacy");
  const t = useMemo(() => STRINGS[lang], [lang]);
  const color = result ? archetypeColor(result.archetype) : "#fff";

  // Firebase Analytics는 즉시(active 상태 불필요).
  useEffect(() => {
    void initAnalytics().then(() => track("app_open"));
  }, []);

  // ★AdMob 초기화(UMP 동의·iOS ATT 모달)는 앱이 포그라운드 active일 때만 모달이 뜬다 →
  //   스플래시 중 호출하면 모달이 '씹혀' 조용히 denied 처리됨. 홈(Vent) 진입 직후 1회만 호출.
  const adsInitRef = useRef(false);
  useEffect(() => {
    if (phase !== "vent" || adsInitRef.current) return;
    adsInitRef.current = true;
    // Vent 페인트·active 안정화 후 호출(클린업으로 취소하지 않음 — 빠른 이탈해도 초기화는 보장).
    setTimeout(() => void initAds(), 500);
  }, [phase]);

  // 안드로이드 하드웨어 뒤로가기 — phase별로 뒤로 이동(웹/iOS엔 영향 없음).
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  useEffect(() => {
    if (!isNative()) return;
    let handle: { remove: () => void } | undefined;
    void CapacitorApp.addListener("backButton", () => {
      switch (phaseRef.current) {
        case "settings":
          setPhase("vent");
          break;
        case "legal":
          setPhase("settings");
          break;
        case "detail":
        case "share":
          setPhase("report");
          break;
        case "report":
        case "bowout":
        case "crisis":
          reset();
          break;
        case "vent":
        case "splash":
          void CapacitorApp.exitApp();
          break;
        // crumple·loading·ad: 전환 중이라 무시
        default:
          break;
      }
    }).then((h) => {
      handle = h;
    });
    return () => handle?.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setLang(l: Lang) {
    setLangState(l);
    localStorage.setItem("bwek_lang", l);
  }

  function handleSubmit(s: RawSignals) {
    setSignals(s);
    localStorage.removeItem("bwek_draft"); // 제출 즉시 초안 삭제 — "저장 안 함" 준수
    track("rant_submitted", { lang, length: lengthBucket(s.charCount), mode: s.mode });
    setPhase("crumple");
  }

  // 광고 게이트(§13) = 전면광고. 상세 보기·자랑하기(카드 보기) 둘 다 광고를 거친다.
  // 자랑하기를 게이트로 둔 이유: 카드가 뜬 뒤 광고를 걸면 스크린샷으로 우회되므로, '카드 보기 전'에 건다.
  function goAd(next: "detail" | "share") {
    setAdNext(next);
    // 웹: Auto ads(AdSense)가 광고를 알아서 띄우므로 게이트 화면을 건너뛴다(빈 플레이스홀더 깜빡임 제거).
    //     네이티브만 AdMob 전면을 거친다.
    if (!isNative()) {
      if (next === "detail") track("detail_viewed");
      setPhase(next);
      return;
    }
    track("ad_shown", { gate: next, format: "interstitial" });
    setPhase("ad");
  }

  // 구겨 버리기 연출 끝 → 로딩에서 판정 실행.
  async function handleCrumpleDone() {
    setPhase("loading");
    if (!signals) return;
    const r = await runVerdict(signals, lang);
    setResult(r);
  }

  // 로딩 최소 노출 후 분기.
  function handleLoadingDone() {
    if (!result) return;
    if (result.judgement.crisis) {
      track("crisis_shown", { lang });
      setPhase("crisis");
    } else if (result.judgement.off_scope) {
      track("offscope_shown", { lang });
      setPhase("bowout");
    } else {
      track("verdict_shown", { score: result.score, archetype: result.archetype, lang });
      setPhase("report");
    }
  }

  function reset() {
    setSignals(null);
    setResult(null);
    setPhase("vent");
  }

  return (
    <div className="app" style={{ ["--pop" as string]: color }}>
      {phase === "splash" && <Splash lang={lang} onDone={() => setPhase("vent")} />}
      {phase === "vent" && (
        <Vent t={t} lang={lang} setLang={setLang} onSettings={() => setPhase("settings")} onSubmit={handleSubmit} />
      )}
      {phase === "settings" && (
        <Settings
          t={t}
          lang={lang}
          setLang={setLang}
          onBack={() => setPhase("vent")}
          onOpenLegal={(k) => {
            setLegalKind(k);
            setPhase("legal");
          }}
        />
      )}
      {phase === "legal" && (
        <Legal t={t} lang={lang} kind={legalKind} onBack={() => setPhase("settings")} />
      )}
      {phase === "crumple" && <Crumple t={t} lang={lang} onDone={handleCrumpleDone} />}
      {phase === "loading" && <Loading t={t} ready={result !== null} onDone={handleLoadingDone} />}
      {phase === "report" && result && (
        <Report
          t={t}
          lang={lang}
          result={result}
          onDetail={() => goAd("detail")}
          onShare={() => goAd("share")}
          onClose={reset}
        />
      )}
      {phase === "detail" && result && (
        <Detail t={t} lang={lang} result={result} onShare={() => goAd("share")} onBack={() => setPhase("report")} />
      )}
      {phase === "ad" && (
        <Ad
          onDone={() => {
            if (adNext === "detail") track("detail_viewed");
            setPhase(adNext);
          }}
        />
      )}
      {phase === "share" && result && (
        <ShareCard t={t} lang={lang} result={result} onReplay={reset} onBack={() => setPhase("report")} />
      )}
      {phase === "bowout" && (
        <BowOut t={t} stepBack={result?.judgement.step_back ?? ""} onClose={reset} />
      )}
      {phase === "crisis" && <Crisis t={t} lang={lang} onClose={reset} />}

      {/* 웹 전용: 스토어 설치 유도(네이티브앱·토스에선 안 뜸). 모든 화면 위에 떠 있음. */}
      <InstallFab t={t} />
    </div>
  );
}
